import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Phone, Lock, KeyRound, Eye, EyeOff, ChevronDown } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { COUNTRIES, DEFAULT_COUNTRY, cleanLocal, buildE164 } from "../lib/countries";

/*
 * Auth flow:
 *   step "phone"          → /auth/check-phone
 *     ├─ new user → step "otp" (register) → step "set-password" → /auth/register
 *     └─ existing user → step "password" → /auth/login
 *        └─ "Forgot password?" → step "otp" (reset) → step "reset-password" → /auth/reset-password
 *
 * IMPORTANT: All sub-component JSX is rendered INLINE inside the single component below.
 * Do NOT extract sub-components inside the function body — that re-creates them on every
 * render and causes the inputs to lose focus on each keystroke.
 */
const LoginModal = ({ open, onClose, onSuccess }) => {
    const { t } = useI18n();
    const { checkPhone, sendOTP, register, login, resetPassword } = useAuth();

    const [step, setStep] = useState("phone");
    const [mode, setMode] = useState("register"); // "register" | "reset" | "login"
    const [country, setCountry] = useState(DEFAULT_COUNTRY);
    const [countryPickerOpen, setCountryPickerOpen] = useState(false);
    const [localPhone, setLocalPhone] = useState("");
    const [normalizedPhone, setNormalizedPhone] = useState("");
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [devOtp, setDevOtp] = useState(null);
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const otpInputRef = useRef(null);
    const passwordInputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setStep("phone");
            setMode("register");
            setCountry(DEFAULT_COUNTRY);
            setCountryPickerOpen(false);
            setLocalPhone("");
            setNormalizedPhone("");
            setCode("");
            setPassword("");
            setName("");
            setDevOtp(null);
            setShowPw(false);
            setLoading(false);
        }
    }, [open]);

    useEffect(() => {
        if (step === "otp" && otpInputRef.current) otpInputRef.current.focus();
        if ((step === "password" || step === "set-password" || step === "reset-password")
            && passwordInputRef.current) {
            passwordInputRef.current.focus();
        }
    }, [step]);

    const triggerOTP = useCallback(async (targetPhone, purpose) => {
        const res = await sendOTP(targetPhone, purpose);
        setNormalizedPhone(res.phone);
        if (res.dev_otp) {
            setDevOtp(res.dev_otp);
            setCode(res.dev_otp);
        } else {
            setDevOtp(null);
            setCode("");
        }
        setStep("otp");
        toast.success(t("toast_otp_sent"));
    }, [sendOTP, t]);

    if (!open) return null;

    const handlePhoneSubmit = async (e) => {
        e?.preventDefault();
        const localDigits = cleanLocal(localPhone);
        if (!localDigits) return;
        const fullPhone = buildE164(country.dial, localDigits);
        setLoading(true);
        try {
            const info = await checkPhone(fullPhone);
            setNormalizedPhone(info.phone);
            if (info.exists && info.has_password) {
                setMode("login");
                setStep("password");
            } else {
                setMode("register");
                await triggerOTP(info.phone, "register");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not verify phone. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        setLoading(true);
        try {
            setMode("reset");
            await triggerOTP(normalizedPhone, "reset");
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast_otp_send_error"));
        } finally {
            setLoading(false);
        }
    };

    const handleOtpContinue = (e) => {
        e?.preventDefault();
        if (code.length !== 6) return;
        setStep(mode === "reset" ? "reset-password" : "set-password");
    };

    const handleLogin = async (e) => {
        e?.preventDefault();
        if (!password) return;
        setLoading(true);
        try {
            await login(normalizedPhone, password);
            toast.success(t("toast_login_success"));
            onSuccess?.();
            onClose();
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            if (status === 409) {
                toast(detail || t("login_legacy_needs_setup"));
                await handleForgotPassword();
            } else {
                toast.error(detail || "Invalid phone or password");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e?.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        try {
            await register(normalizedPhone, code, password, name || null);
            toast.success(t("toast_login_success"));
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not create account");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e?.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        try {
            await resetPassword(normalizedPhone, code, password);
            toast.success(t("toast_login_success"));
            onSuccess?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not reset password");
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        setStep("phone");
        setCode("");
        setPassword("");
        setDevOtp(null);
    };

    return (
        <div
            data-testid="login-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm fade-up"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white rounded-3xl w-full max-w-md p-7 md:p-9 shadow-2xl relative">
                <button
                    data-testid="login-modal-close"
                    onClick={onClose}
                    className="absolute top-5 right-5 h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors"
                    aria-label="Close"
                >
                    <X className="h-4 w-4" />
                </button>

                {/* ─── Step 1: phone ─── */}
                {step === "phone" && (
                    <form onSubmit={handlePhoneSubmit}>
                        <div className="h-12 w-12 rounded-2xl bg-[#FFF3EE] text-[#FF5722] flex items-center justify-center">
                            <Phone className="h-6 w-6" strokeWidth={1.75} />
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-5">
                            {t("login_title")}
                        </h2>
                        <p className="text-neutral-600 mt-2 text-[15px]">{t("login_subtitle")}</p>
                        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-7 mb-2">
                            {t("login_phone_label")}
                        </label>
                        <div className="flex gap-2 relative">
                            <button
                                type="button"
                                data-testid="login-country-toggle"
                                onClick={() => setCountryPickerOpen((v) => !v)}
                                className="h-14 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none inline-flex items-center gap-1.5 shrink-0 hover:border-neutral-300 transition-all"
                            >
                                <span className="text-xl leading-none">{country.flag}</span>
                                <span className="font-semibold text-sm">{country.dial}</span>
                                <ChevronDown className={`h-3.5 w-3.5 text-neutral-500 transition-transform ${countryPickerOpen ? "rotate-180" : ""}`} />
                            </button>
                            <input
                                data-testid="login-phone-input"
                                type="tel"
                                inputMode="numeric"
                                autoFocus
                                required
                                value={localPhone}
                                onChange={(e) => setLocalPhone(e.target.value.replace(/[^\d\s\-]/g, "").slice(0, 15))}
                                placeholder={country.placeholder}
                                className="flex-1 min-w-0 h-14 px-5 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none text-base transition-all"
                            />
                            {countryPickerOpen && (
                                <div
                                    data-testid="login-country-dropdown"
                                    className="absolute top-16 left-0 z-20 bg-white rounded-2xl shadow-xl border border-neutral-200 max-h-64 overflow-y-auto w-72"
                                >
                                    {COUNTRIES.map((c) => {
                                        const isDisabled = c.iso !== "AL";
                                        return (
                                            <button
                                                key={c.iso}
                                                type="button"
                                                data-testid={`login-country-${c.iso}`}
                                                onClick={() => {
                                                    if (isDisabled) {
                                                        toast(`${c.name}: ${t("category_coming_soon")}`);
                                                        return;
                                                    }
                                                    setCountry(c);
                                                    setCountryPickerOpen(false);
                                                }}
                                                disabled={isDisabled}
                                                aria-disabled={isDisabled}
                                                title={isDisabled ? t("category_coming_soon") : undefined}
                                                className={`w-full px-4 py-2.5 flex items-center gap-3 text-left text-sm transition-colors ${
                                                    isDisabled
                                                        ? "text-neutral-400 cursor-not-allowed opacity-60"
                                                        : `hover:bg-neutral-50 ${country.iso === c.iso ? "bg-[#FFF3EE] text-[#FF5722] font-semibold" : "text-neutral-800"}`
                                                }`}
                                            >
                                                <span className={`text-xl leading-none ${isDisabled ? "grayscale" : ""}`}>{c.flag}</span>
                                                <span className="flex-1 min-w-0 truncate">{c.name}</span>
                                                {isDisabled ? (
                                                    <span className="text-[9px] font-bold uppercase tracking-wider bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded-full">
                                                        {t("category_coming_soon")}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-neutral-500 font-mono">{c.dial}</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !cleanLocal(localPhone)}
                            data-testid="login-continue-btn"
                            className="mt-5 w-full h-14 rounded-xl bg-[#1a1a1a] hover:bg-[#FF5722] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? t("login_sending") : (
                                <>{t("login_continue")} <ArrowRight className="h-5 w-5" /></>
                            )}
                        </button>
                    </form>
                )}

                {/* ─── Step 2a: password (existing user) ─── */}
                {step === "password" && (
                    <form onSubmit={handleLogin}>
                        <button
                            type="button"
                            onClick={goBack}
                            data-testid="login-back-btn"
                            className="text-sm text-neutral-600 hover:text-[#FF5722] inline-flex items-center gap-1 mb-3"
                        >
                            <ArrowLeft className="h-4 w-4" /> {t("login_change_number")}
                        </button>
                        <div className="h-12 w-12 rounded-2xl bg-[#FFF3EE] text-[#FF5722] flex items-center justify-center">
                            <Lock className="h-6 w-6" strokeWidth={1.75} />
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-5">
                            {t("login_password_signin_title")}
                        </h2>
                        <p className="text-neutral-600 mt-2 text-[15px]">
                            {t("login_password_signin_subtitle")} <span className="font-semibold text-neutral-900">{normalizedPhone}</span>
                        </p>
                        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-7 mb-2">
                            {t("login_password_label")}
                        </label>
                        <div className="relative">
                            <input
                                ref={passwordInputRef}
                                data-testid="login-password-input"
                                type={showPw ? "text" : "password"}
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={t("login_password_placeholder")}
                                className="w-full h-14 px-5 pr-14 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none text-base transition-all"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                data-testid="login-toggle-password-visibility"
                                className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                                aria-label="Toggle password visibility"
                            >
                                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !password}
                            data-testid="login-signin-btn"
                            className="mt-5 w-full h-14 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? t("login_signing_in") : t("login_signin_btn")}
                        </button>
                        <button
                            type="button"
                            onClick={handleForgotPassword}
                            disabled={loading}
                            data-testid="login-forgot-password-btn"
                            className="mt-3 w-full h-11 rounded-xl text-sm text-neutral-600 hover:text-[#FF5722] font-medium inline-flex items-center justify-center gap-1.5 transition-colors"
                        >
                            <KeyRound className="h-3.5 w-3.5" />
                            {t("login_forgot_password")}
                        </button>
                    </form>
                )}

                {/* ─── Step 2b: OTP ─── */}
                {step === "otp" && (
                    <form onSubmit={handleOtpContinue}>
                        <button
                            type="button"
                            onClick={goBack}
                            data-testid="login-back-btn"
                            className="text-sm text-neutral-600 hover:text-[#FF5722] inline-flex items-center gap-1 mb-3"
                        >
                            <ArrowLeft className="h-4 w-4" /> {t("login_change_number")}
                        </button>
                        <div className="h-12 w-12 rounded-2xl bg-[#FFF3EE] text-[#FF5722] flex items-center justify-center">
                            <KeyRound className="h-6 w-6" strokeWidth={1.75} />
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-5">
                            {mode === "reset"
                                ? t("login_otp_reset_title")
                                : t("login_otp_register_title")}
                        </h2>
                        <p className="text-neutral-600 mt-2 text-[15px]">
                            {mode === "reset"
                                ? t("login_otp_reset_subtitle")
                                : t("login_otp_register_subtitle")}
                            {" "}
                            <span className="font-semibold text-neutral-900">{normalizedPhone}</span>
                        </p>
                        {devOtp && (
                            <div
                                data-testid="dev-otp-hint"
                                className="mt-5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm"
                            >
                                <span className="font-semibold">{t("login_dev_hint")}</span>{" "}
                                <span className="font-mono font-bold tracking-widest">{devOtp}</span>
                            </div>
                        )}
                        <input
                            ref={otpInputRef}
                            data-testid="login-otp-input"
                            type="text"
                            inputMode="numeric"
                            pattern="\d{6}"
                            maxLength={6}
                            required
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="••••••"
                            className="mt-5 w-full h-16 px-5 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none text-2xl font-mono tracking-[0.5em] text-center transition-all"
                        />
                        <button
                            type="submit"
                            disabled={loading || code.length !== 6}
                            data-testid="login-verify-btn"
                            className="mt-5 w-full h-14 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {t("login_verify")}
                        </button>
                        <button
                            type="button"
                            onClick={() => triggerOTP(normalizedPhone, mode === "reset" ? "reset" : "register")}
                            disabled={loading}
                            data-testid="login-resend-otp-btn"
                            className="mt-3 w-full h-11 rounded-xl text-sm text-neutral-600 hover:text-[#FF5722] font-medium transition-colors"
                        >
                            {t("login_resend")}
                        </button>
                    </form>
                )}

                {/* ─── Step 3: set-password OR reset-password ─── */}
                {(step === "set-password" || step === "reset-password") && (
                    <form onSubmit={mode === "reset" ? handleResetPassword : handleRegister}>
                        <button
                            type="button"
                            onClick={() => setStep("otp")}
                            data-testid="login-back-btn"
                            className="text-sm text-neutral-600 hover:text-[#FF5722] inline-flex items-center gap-1 mb-3"
                        >
                            <ArrowLeft className="h-4 w-4" /> {t("login_back")}
                        </button>
                        <div className="h-12 w-12 rounded-2xl bg-[#FFF3EE] text-[#FF5722] flex items-center justify-center">
                            <Lock className="h-6 w-6" strokeWidth={1.75} />
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-5">
                            {mode === "reset" ? t("login_reset_title") : t("login_set_password_title")}
                        </h2>
                        <p className="text-neutral-600 mt-2 text-[15px]">
                            {mode === "reset"
                                ? <>{t("login_otp_subtitle")} <span className="font-semibold text-neutral-900">{normalizedPhone}</span></>
                                : t("login_set_password_subtitle")}
                        </p>

                        {mode === "register" && (
                            <>
                                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-6 mb-2">
                                    {t("login_name_label")}
                                </label>
                                <input
                                    data-testid="login-name-input"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full h-12 px-4 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none transition-all"
                                />
                            </>
                        )}

                        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-4 mb-2">
                            {t("login_password_label")}
                        </label>
                        <div className="relative">
                            <input
                                ref={passwordInputRef}
                                data-testid="login-new-password-input"
                                type={showPw ? "text" : "password"}
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={t("login_password_placeholder")}
                                className="w-full h-14 px-5 pr-14 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/15 outline-none text-base transition-all"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                                aria-label="Toggle password visibility"
                            >
                                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || password.length < 6}
                            data-testid={mode === "reset" ? "login-reset-btn" : "login-register-btn"}
                            className="mt-5 w-full h-14 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white font-semibold inline-flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading
                                ? (mode === "reset" ? t("login_signing_in") : t("login_creating"))
                                : (mode === "reset" ? t("login_reset_btn") : t("login_create_account_btn"))}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default LoginModal;
